import { useState, useEffect } from 'react';
import styled from 'styled-components';
import InputField from './InputField';
import Spinner from './Spinner';
import Alert from './Alert';
import Button from './Button';
import Modal from './Modal';
import { useAuth } from './Authentication/AuthProvider';
import { getRecipientCount, sendPushNotification } from '../api/pushNotificationsAPI';
import { COLORS } from '../utils/constants/colors';

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0 0.5rem 0.5rem;
`;
const Label = styled.label`
  color: ${COLORS.darkGray};
  font-size: 0.8rem;
  font-weight: bold;
`;
const StyledSelect = styled.select`
  border: 1px solid ${COLORS.lightGray};
  border-radius: 0.25rem;
  padding: 0.5rem;
  width: 100%;
  color: ${COLORS.darkGray};
  background-color: ${COLORS.white};
`;
const Textarea = styled.textarea`
  border: 1px solid ${COLORS.lightGray};
  border-radius: 0.25rem;
  padding: 0.5rem;
  width: 94%;
  color: ${COLORS.darkGray};
  background-color: ${COLORS.white};
  resize: vertical;
  min-height: 6rem;
  font-family: inherit;
  font-size: inherit;
`;
const RecipientHint = styled.p`
  font-size: 0.8rem;
  color: ${COLORS.gray};
  padding-left: 0.5rem;
  margin: 0;
`;
const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 1rem;
  margin-top: 0.5rem;
  border-top: solid 1px ${COLORS.lighterGray};
`;
const ButtonCentered = styled(Button)`
  width: fit-content !important;
`;
const Form = styled.form`
  padding: 0 0.5rem 0 0;
`;
const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ConfirmText = styled.p`
  color: ${COLORS.darkGray};
  margin: 0 0 0.75rem;
  line-height: 1.5;
`;

const ConfirmSummary = styled.div`
  font-size: 0.85rem;
  color: ${COLORS.gray};
  margin-top: 0.5rem;
  padding: 0.75rem;
  background-color: ${COLORS.lighterGray};
  border-radius: 0.25rem;

  strong {
    color: ${COLORS.darkGray};
  }
`;

const AUDIENCES = [
  { value: 'all', label: 'All devices' },
  { value: 'android', label: 'Android only' },
  { value: 'ios', label: 'iOS only' },
];

const NewNotification = ({ onNotificationSent }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('all');
  const [recipientCount, setRecipientCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmRecipientCount, setConfirmRecipientCount] = useState(null);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const { token } = useAuth();

  const audienceLabel =
    AUDIENCES.find((item) => item.value === audience)?.label ?? audience;

  const isFormValid = title.trim().length > 0 && body.trim().length > 0;

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      const count = await getRecipientCount(token, audience);
      if (!cancelled) setRecipientCount(count);
    };
    fetch();
    return () => { cancelled = true; };
  }, [audience, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsConfirmationOpen(true);
    const count = await getRecipientCount(token, audience);
    setConfirmRecipientCount(count);
  };

  const handleCloseConfirmation = () => {
    setIsConfirmationOpen(false);
    setConfirmRecipientCount(null);
  };

  const handleConfirmSend = async () => {
    setIsConfirmationOpen(false);
    setConfirmRecipientCount(null);
    setIsLoading(true);
    const response = await sendPushNotification(token, { title, body, audience });
    if (response?.status === 200) {
      setIsError(false);
      setMessage('Notification sent successfully.');
      setTitle('');
      setBody('');
      setAudience('all');
      onNotificationSent?.();
    } else {
      setIsError(true);
      const code = response?.data?.errors?.[0]?.code;
      setMessage(code ? code.charAt(0).toUpperCase() + code.slice(1) : 'Failed to send notification.');
    }
    setIsLoading(false);
  };

  return (
    <>
    <Form onSubmit={handleSubmit}>
      <Rows>
        <InputField
          label="Title"
          name="title"
          type="text"
          placeholder="e.g. Important notice"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setMessage(''); }}
        />
        <FormGroup>
          <Label htmlFor="body">Message</Label>
          <Textarea
            id="body"
            name="body"
            placeholder="e.g. Message body."
            value={body}
            onChange={(e) => { setBody(e.target.value); setMessage(''); }}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="audience">Audience</Label>
          <StyledSelect
            id="audience"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
          >
            {AUDIENCES.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </StyledSelect>
        </FormGroup>
        {recipientCount !== null && (
          <RecipientHint>{recipientCount} device(s) will receive this notification.</RecipientHint>
        )}
        {message && (isError ? <Alert error>{message}</Alert> : <Alert success>{message}</Alert>)}
        <ButtonGroup>
          <ButtonCentered type="submit" disabled={!isFormValid}>
            Send notification
          </ButtonCentered>
        </ButtonGroup>
      </Rows>
      {isLoading && <Spinner />}
    </Form>
    <Modal
      title="Confirm notification"
      isModalOpen={isConfirmationOpen}
      onCloseModal={handleCloseConfirmation}
      onSave={handleConfirmSend}
      cancelText="Cancel"
      saveText="Send notification"
      disableSave={confirmRecipientCount === null || confirmRecipientCount === 0}
      width="28rem"
    >
      {confirmRecipientCount === null ? (
        <ConfirmText>Loading recipient count…</ConfirmText>
      ) : (
        <>
          <ConfirmText>
            <strong>{confirmRecipientCount}</strong> device(s) will receive this
            notification ({audienceLabel}).
          </ConfirmText>
          <ConfirmText>Do you want to send it?</ConfirmText>
          <ConfirmSummary>
            <div><strong>Title:</strong> {title}</div>
            <div><strong>Message:</strong> {body}</div>
          </ConfirmSummary>
        </>
      )}
    </Modal>
    </>
  );
};

export default NewNotification;
